import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"

import { isLikelyMarkdown } from "@/tiptap/core/markdown/markdown-detect"
import { normalizeMarkdownDocument } from "@/tiptap/core/markdown/markdown-normalize"

export const MARKDOWN_PASTE_KEY = new PluginKey("markdownPaste")

// Tags that indicate the clipboard HTML is already structured rich content we
// should paste as-is rather than re-parsing the plain text as markdown.
const RICH_STRUCTURE_SELECTOR = [
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "table", "thead", "tbody", "tr", "td", "th",
  "blockquote", "hr", "figure", "img", "iframe", "video", "audio",
  "a", "strong", "em", "b", "i", "u", "s", "del", "mark", "sub", "sup",
].join(",")

// Browsers copying a "raw" text view (e.g. GitHub's Raw button) wrap the whole
// document in a single <pre>, so the clipboard carries text/html even though
// the content is really just plain markdown. We only defer to the HTML paste
// path when the HTML contains genuinely rich structure; a bare <pre>/plain-text
// wrapper is treated as markdown so pasted markdown converts as expected.
export function htmlHasRichStructure(html) {
  if (!html || !html.trim()) return false
  if (typeof document === "undefined") return true

  const container = document.createElement("div")
  container.innerHTML = html

  return container.querySelector(RICH_STRUCTURE_SELECTOR) != null
}

function selectionIsInsideCallout(selection) {
  const { $from } = selection

  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === "callout") return true
  }

  return false
}

function selectionIsInsideCodeBlock(selection) {
  const { $from } = selection

  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth)
    if (node.type.spec.code || node.type.name === "codeBlock") return true
  }

  return false
}

// Auto-detects markdown in pasted plain text and converts it to rich content.
// Only fires when the clipboard has plain text but no meaningful HTML (so rich
// copies keep their normal behavior) and the text looks like markdown. Inside a
// callout or code block we defer to the default plain-text paste so mermaid
// frontmatter (`---`) is not converted into headings/hrs below the block.
export const MarkdownPaste = Extension.create({
  name: "markdownPaste",

  addProseMirrorPlugins() {
    const editor = this.editor

    return [
      new Plugin({
        key: MARKDOWN_PASTE_KEY,
        props: {
          handlePaste: (view, event) => {
            const clipboard = event.clipboardData
            if (!clipboard) return false

            const text = clipboard.getData("text/plain")
            if (!text || !text.trim()) return false

            // Genuinely rich HTML (headings, lists, tables, links, etc.) is
            // pasted as-is; a bare <pre>/plain-text wrapper falls through so
            // raw markdown copied from a browser view still converts.
            const html = clipboard.getData("text/html")
            if (htmlHasRichStructure(html)) return false

            if (!isLikelyMarkdown(text)) return false
            if (selectionIsInsideCallout(view.state.selection)) return false
            // Code blocks are raw text: mermaid source (`---` frontmatter) looks
            // like markdown and would otherwise be converted into headings/hrs
            // and inserted below the block.
            if (selectionIsInsideCodeBlock(view.state.selection)) return false

            const manager = editor.markdown
            if (!manager || typeof manager.parse !== "function") return false

            // Parse and normalize the document so block-level nodes the parser
            // emits inline (e.g. images) don't break the schema on insert. On
            // any failure we return false so the default paste still happens.
            try {
              const parsed = manager.parse(text)
              const normalized = normalizeMarkdownDocument(parsed, editor.schema)
              const content = normalized?.content ?? normalized

              const handled = editor
                .chain()
                .focus()
                .insertContent(content)
                .run()

              return handled !== false
            } catch {
              return false
            }
          },
        },
      }),
    ]
  },
})

export default MarkdownPaste
